import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material'
import { groupBy } from 'lodash'
import { useContext, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { itemsActions } from 'src/ducks/actions'
import { WorkOrdersContext } from '../../../context'
import styles from './styles.module.scss'

type Props = {}

interface ReorderedItem {
    id: string;
    oldIndex: number;
    newIndex: number;
    title: string;
    sortIndex: number;
}

const ReorderItems = (props: Props) => {
    const { state: { selectedItemsToReorder }, dispatch: dispatchContext } = useContext(WorkOrdersContext)
    const [reorderedItems, setReorderedItems] = useState<ReorderedItem[]>([])
    const [isSaving, setIsSaving] = useState(false)

    const dispatch = useDispatch()
    const { id: estimateId } = useParams()

    const orderItemsByCategory = groupBy(selectedItemsToReorder, 'category')

    const onDragEnd = (result: any) => {
        const { source, destination } = result

        // If there is no destination or the destination is the same as the source, do nothing
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
            return
        }

        // Only allow reordering within the same category
        if (source.droppableId !== destination.droppableId) {
            return
        }

        // Get the category
        const category = source.droppableId
        const items = [...orderItemsByCategory[category]]

        // Remove the item from the original position
        const [removed] = items.splice(source.index, 1)
        // Insert the item in the new position
        items.splice(destination.index, 0, removed)

        // Update the state with the reordered items
        const newOrderItemsByCategory = {
            ...orderItemsByCategory,
            [category]: items
        }

        // Convert the grouped object back to a flat array
        const newSelectedItemsToReorder = Object.values(newOrderItemsByCategory).flat()


        // Actualizar el sortIndex de cada item basado en su posición
        const updatedItems = newSelectedItemsToReorder.map((item: any, index) => {
            const updatedItem = { ...item }
            if (index === 0) {
                // Si es el primer item, mantener su sortIndex actual
                return updatedItem
            } else {
                // Para los siguientes items, usar el sortIndex del item anterior + 1
                const previousItem = newSelectedItemsToReorder[index - 1] as { sortIndex: number }
                updatedItem.sortIndex = previousItem.sortIndex + 1
                return updatedItem
            }
        })

        const updatedItemsByCategory = groupBy(updatedItems, 'category')

        const updatedItemCategoryFilter = updatedItemsByCategory[category]
        const updatedItemIndex = updatedItemCategoryFilter.findIndex((item: any) => item.id === removed.id)
        const itemBeforeUpdatedItemSortIndex = (updatedItemCategoryFilter[updatedItemIndex - 1]?.sortIndex || 0) + 1
        // Registramos el cambio en el historial
        const newReorderedItem: ReorderedItem = {
            id: removed.id?.toString() || '',
            oldIndex: source.index,
            newIndex: itemBeforeUpdatedItemSortIndex,
            sortIndex: removed.sortIndex,
            title: removed.title || 'No Name'
        }
        setReorderedItems(prev => [...prev, newReorderedItem])

        dispatchContext({ type: 'SET_SELECTED_ITEMS_TO_REORDER', payload: updatedItems })
    }

    const updateItem = (item: any) => {
        return new Promise((resolve, reject) => {
            try {
                const action = itemsActions.updateItemByPath({
                    itemId: item.id,
                    request: [
                        {
                            op: 'add',
                            path: '/sortIndex',
                            value: item.newIndex
                        }
                    ]
                }, (success) => {
                    if (success) {
                        resolve(true)
                    } else {
                        reject(new Error(`Failed to update item ${item.id}`))
                    }
                })

                // Make sure the action is dispatched
                dispatch(action)
            } catch (error) {
                console.error('Error in updateItem:', error)
                reject(error)
            }
        })
    }

    const handleSaveChanges = async () => {
        try {
            setIsSaving(true)

            // Process the items sequentially to avoid concurrency problems
            const results: unknown[] = []
            for (const item of reorderedItems) {
                try {
                    const result = await updateItem(item)
                    results.push(result)
                } catch (error) {
                    console.error(`Error updating item ${item.id}:`, error)
                    throw error
                }
            }


            if (results.every((success) => success)) {
                toast.success('Changes saved successfully')
                dispatch(itemsActions.fetchItemsByCategory({ params: { jobId: estimateId } }, () => {
                    dispatchContext({ type: 'SET_MODAL_TYPE', payload: null })
                    dispatchContext({ type: 'SET_MODAL_OPEN', payload: false })
                }))
            } else {
                toast.error('Error trying to save changes')
            }
        } catch (error) {
            console.error('Error in handleSaveChanges:', error)
            toast.error('Error trying to save changes')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Box className={styles.root}>
            <Box className={styles.container}>
                <DragDropContext onDragEnd={onDragEnd}>
                    {Object.entries(orderItemsByCategory).map(([category, items]) => (
                        <Box key={category} className={styles.categoryContainer}>
                            <Typography variant="h6" className={styles.categoryTitle}>
                                {category}
                            </Typography>
                            <Droppable droppableId={category}>
                                {(provided) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={styles.itemsContainer}
                                    >
                                        {items.map((item, index) => (
                                            <Draggable
                                                key={item.id || `item-${index}`}
                                                draggableId={item.id?.toString() || `item-${index}`}
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <Paper
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={styles.item}
                                                    >
                                                        <Typography>{`${item.inspectionReportReference ? `Ref: ${item.inspectionReportReference} - ` : ''} ${item.title || 'No Name'}`}</Typography>
                                                    </Paper>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    ))}
                </DragDropContext>
            </Box>
            <Box className={styles.buttonContainer}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveChanges}
                    disabled={isSaving || reorderedItems.length === 0}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    <Typography>{isSaving ? 'Saving...' : 'Save Changes'}</Typography>
                </Button>
            </Box>
        </Box>
    )
}

export default ReorderItems